/** Filter bar for the Labour Management demo screen - date (cosmetic, single day of mock
 * data), site/region, sector, service period and view mode. Site/sector/service-period and
 * view selections are wired to filter the tables/sections below; the date picker is
 * illustrative only since there is no backend feed with multiple days of data. */

import { Grid, MenuItem, Stack, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SECTORS, SERVICE_PERIODS, SITES } from '../data';
import { ViewMode } from '../types';

export interface LabourFilters {
  date: string;
  site: string;
  sector: string;
  servicePeriod: string;
  view: ViewMode;
}

interface FiltersBarProps {
  filters: LabourFilters;
  onChange: (filters: LabourFilters) => void;
}

const ALL = 'All';

const FiltersBar = ({ filters, onChange }: FiltersBarProps) => {
  const { t } = useTranslation('common');

  return (
    <Stack sx={{ mt: 3, mb: 3 }}>
      <Grid container
        spacing={2}>
        <Grid item
          xs={12}
          sm={6}
          md={2.4}>
          <TextField label={t('labourManagementDate', 'Date')}
            type="date"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={filters.date}
            onChange={(e) => onChange({ ...filters, date: e.target.value })} />
        </Grid>
        <Grid item
          xs={12}
          sm={6}
          md={2.4}>
          <TextField label={t('labourManagementSiteFilter', 'Site / region')}
            select
            size="small"
            fullWidth
            value={filters.site}
            onChange={(e) => onChange({ ...filters, site: e.target.value })}>
            <MenuItem value={ALL}>{t('labourManagementAllSites', 'All sites')}</MenuItem>
            {SITES.map((site) => (
              <MenuItem key={site}
                value={site}>{site}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item
          xs={12}
          sm={6}
          md={2.4}>
          <TextField label={t('labourManagementSectorFilter', 'Sector')}
            select
            size="small"
            fullWidth
            value={filters.sector}
            onChange={(e) => onChange({ ...filters, sector: e.target.value })}>
            <MenuItem value={ALL}>{t('labourManagementAllSectors', 'All sectors')}</MenuItem>
            {SECTORS.map((sector) => (
              <MenuItem key={sector}
                value={sector}>{sector}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item
          xs={12}
          sm={6}
          md={2.4}>
          <TextField label={t('labourManagementServicePeriodFilter', 'Service period')}
            select
            size="small"
            fullWidth
            value={filters.servicePeriod}
            onChange={(e) => onChange({ ...filters, servicePeriod: e.target.value })}>
            <MenuItem value={ALL}>{t('labourManagementAllServicePeriods', 'All service periods')}</MenuItem>
            {SERVICE_PERIODS.map((period) => (
              <MenuItem key={period}
                value={period}>{period}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item
          xs={12}
          sm={6}
          md={2.4}>
          <TextField label={t('labourManagementViewFilter', 'View')}
            select
            size="small"
            fullWidth
            value={filters.view}
            onChange={(e) => onChange({ ...filters, view: e.target.value as ViewMode })}>
            <MenuItem value="Today">{t('labourManagementViewToday', 'Today')}</MenuItem>
            <MenuItem value="Week">{t('labourManagementViewWeek', 'Week')}</MenuItem>
            <MenuItem value="Exceptions">{t('labourManagementViewExceptions', 'Exceptions')}</MenuItem>
          </TextField>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default FiltersBar;
export { ALL as ALL_FILTER_VALUE };
